import { BusFront, ChevronLeft, Clock3, MapPinned } from "lucide-react";
import { memo } from "react";
import { Button } from "@trotroways/ui";
import type { Station, StationDetails } from "@/lib/api";

export interface StopInfoPanelProps {
  station: Station;
  details: StationDetails | null;
  isLoading: boolean;
  onBack: () => void;
  onRouteToStop: () => void;
}

export const StopInfoPanel = memo(function StopInfoPanel({
  station,
  details,
  isLoading,
  onBack,
  onRouteToStop,
}: StopInfoPanelProps) {
  return (
    <div className="space-y-4 text-white">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="-ml-2 size-8 rounded-full"
          onClick={onBack}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h3 className="truncate text-base font-semibold">{station.name}</h3>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="mb-3 flex items-center gap-2 text-sm text-zinc-200">
          <BusFront className="h-4 w-4 text-emerald-400" />
          Stop overview
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs text-zinc-300">
          <div className="rounded-md bg-black/35 p-2">
            <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-400">Stop ID</p>
            <p className="truncate font-medium text-zinc-100">{station.id || "Unknown"}</p>
          </div>
          <div className="rounded-md bg-black/35 p-2">
            <p className="mb-1 text-[11px] uppercase tracking-wide text-zinc-400">Coordinates</p>
            <p className="font-medium text-zinc-100">
              {station.lat.toFixed(4)}, {station.lon.toFixed(4)}
            </p>
          </div>
        </div>
        <Button
          type="button"
          className="mt-3 w-full"
          onClick={onRouteToStop}
        >
          <MapPinned className="mr-2 h-4 w-4" />
          Route To This Stop
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="mb-3 flex items-center gap-2 text-sm text-zinc-200">
          <Clock3 className="h-4 w-4 text-emerald-400" />
          Routes serving this stop
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <div className="h-8 animate-pulse rounded-md bg-white/10" />
            <div className="h-8 animate-pulse rounded-md bg-white/10" />
            <div className="h-8 animate-pulse rounded-md bg-white/10" />
          </div>
        ) : details && details.routes.length > 0 ? (
          <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
            {details.routes.map((route) => (
              <div
                key={`${route.id}-${route.shortName}-${route.longName}`}
                className="flex items-center gap-2 rounded-md bg-black/35 px-2 py-2"
              >
                <span className="min-w-8 rounded bg-emerald-500/20 px-1.5 py-0.5 text-center text-[11px] font-semibold text-emerald-300">
                  {route.shortName || "#"}
                </span>
                <span className="truncate text-xs text-zinc-100">
                  {route.longName || route.shortName || "Route"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-4 text-center text-sm text-zinc-400">No routes found for this stop</p>
        )}
      </div>
    </div>
  );
});
